/**
 * Keeps the current in-app route across refresh, including when the SW serves index.html.
 */
import React, { useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { isReloadNavigation, isShellPath, readLastPath, writeLastPath } from "./session";

/**
 * Records every real page so a later reload can return here.
 * On refresh, restores the last working page if the URL fell back to the shell or dashboard.
 *
 * @returns {null}
 */
export const LocationKeeper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const restoredRef = useRef(false);

  useEffect(() => {
    const path = `${location.pathname}${location.search}${location.hash}`;

    if (!restoredRef.current && isReloadNavigation()) {
      restoredRef.current = true;
      const last = readLastPath();
      const lastPathname = last.split("?")[0].split("#")[0];
      const fellBack = isShellPath(location.pathname) || location.pathname === "/dashboard";
      if (last && !isShellPath(lastPathname) && last !== path && fellBack && lastPathname !== "/dashboard") {
        navigate(last, { replace: true });
        return;
      }
    }

    writeLastPath(path);
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

/**
 * `/` and `/index.html` always go to the dashboard. Refresh restore is handled
 * by `LocationKeeper`, so the logo/home link must not bounce back to the last page.
 *
 * @returns {JSX.Element}
 */
export const HomeRedirect = () => <Navigate to="/dashboard" replace />;

export default LocationKeeper;
